import { Arr, NullFunction } from '@deqo/foundation';
import React, { useCallback, useRef, useState } from 'react';
import Scrollbars, { positionValues } from 'react-custom-scrollbars';
import { useEffectOnce, useSessionStorage } from 'react-use';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { useSimpleDrag } from '../../../ui/Hooks';
import { PopupTabs } from '../../../ui/PopupTabs';
import { Tip } from '../../../ui/Tip';
import { Flexer, Spacer, Title, TitleLine } from '../../dashboard/elements/LayoutElements';
import { Colors } from './../../../ui';

const XScrollbars = Scrollbars as any;

const ItemPreview = (impl: React.ComponentType<any>, span: number = 1, hover: 'pop' | 'shade' = 'shade') =>
    ({ impl, span, hover });
type ItemPreview = ReturnType<typeof ItemPreview>;

const Item = (id: string, label: string, props: any, preview: ItemPreview) =>
    ({ id, label, props, preview });
type Item = ReturnType<typeof Item>;

// Make this ItemSet
const ItemSet = (id: string, label: string, columns: number, items: Item[]) =>
    ({ id, label, columns, items, type: 'set' });
type ItemSet = ReturnType<typeof ItemSet>;

// Make this ItemGroup
const ItemGroup = (id: string, label: string, icon: string | React.ComponentType, sets: ItemSet[]) =>
    ({ id, label, icon, sets, type: 'group' });
type ItemGroup = ReturnType<typeof ItemGroup>;

const Model = (...m: (ItemGroup)[]) => m;
type Model = ReturnType<typeof Model>;

type ElementSelectorCallback = (item: Item, mode: 'click' | 'drag') => void;
type ElementSelectorProps = {
    name: string,
    model: Model,
    onSelectItem?: ElementSelectorCallback,
    onSelectGroup?: (group: ItemGroup) => void,
};

const Frame = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 23.25rem;
    min-height: 32.5rem;

    ${PopupTabs} {
        margin-bottom: 0;
    }

    ${PopupTabs.Item} {
        width: 3.15rem;
        justify-content: center;
    }
`;

const ItemSetFrame = styled.div<{ columns: number }>`
    display: grid;
    grid-template-columns: repeat(${p => p.columns}, 1fr);
`;

const ItemFrame = styled.div<{ hover: 'pop' | 'shade', span?: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: span ${p => p.span || 1};
    padding: 0.3125rem;
    transition: transform 100ms linear;
    cursor: pointer;
    border-radius: .125rem;
    
    &:hover {
        ${p => p.hover === 'pop' ? 'transform: scale(1.075);' : ''};
        ${p => p.hover === 'shade' ? `background: ${Colors.hover};` : ''};
    }
`;

const ItemMount = React.memo<{ item: Item, onSelect: ElementSelectorCallback }>(({ item, onSelect }) => {
    const { props, preview } = item;
    const Preview = preview.impl;

    const dragStart = useSimpleDrag(useCallback(dist => {
        if (dist.length() > 10) {
            onSelect(item, 'drag');
            return false;
        }
        return true;
    }, [onSelect]));

    const onClick = useCallback((e: any) => {
        e.preventDefault();
        onSelect(item, 'click');
    }, [onSelect]);

    return <ItemFrame
        title={item.label}
        hover={item.preview.hover}
        span={item.preview.span}
        onMouseDown={dragStart}
        onClick={onClick}
    >
        <Preview {...props} />
    </ItemFrame>;
});

const getAnchorForScroll = (anchors: React.RefObject<HTMLDivElement>[], values: positionValues) => {
    if (values.top === 0) return Arr.first(anchors)?.current;
    if (values.top >= 1) return Arr.last(anchors)?.current;

    const includeLine = values.scrollTop + (values.clientHeight * .25);
    const elmts = anchors.map(a => a.current).filter(x => x.offsetTop <= includeLine);
    const dist = (x: HTMLDivElement) => Math.abs(x.offsetTop - includeLine);

    const [hit] = elmts.sort((a, b) => dist(a) - dist(b));
    return hit ?? null;
};

const Impl: LFC<ElementSelectorProps> = ({ name, model, onSelectItem, onSelectGroup, children }) => {
    onSelectItem = onSelectItem ?? NullFunction;
    onSelectGroup = onSelectGroup ?? NullFunction;

    const [activeTab, setActiveTab] = useState(0);
    const [initialScroll, setInitialScroll] = useSessionStorage(`deqo/ui/elementSelector/${name}`, 0);

    const scroller = useRef<any>();
    const anchors = model.map(() => useRef<HTMLDivElement>());

    const onScrollGoto = useCallback((index: number) => {
        const a = anchors[index]?.current;
        if (!a) return;
        const y = a.offsetTop - (index === model.length - 1 ? 0 : 1);
        scroller.current.scrollTop(y);
        // Unless at the very end, scroll up by 1px as this fixes some situations where
        // the scroll area is not big enough causing the wrong tab to show
        if (index !== model.length - 1) {
            scroller.current.scrollTop(scroller.current.getScrollTop() - 1);
        }
    }, [scroller, anchors]);

    const onScroll = useCallback((values: positionValues) => {
        const a = getAnchorForScroll(anchors, values);
        if (a) {
            const nextTab = Math.max(0, Number(a.dataset.tab));
            setActiveTab(nextTab);
            onSelectGroup(model[nextTab]);
        }
        setInitialScroll(values.scrollTop);
    }, [model, onSelectGroup, activeTab, anchors]);

    useEffectOnce(() => scroller.current.scrollTop(initialScroll));

    return <Frame>
        <PopupTabs padding=".5 .5 0" spacing=".5">
            {model.map((gp, i) =>
                <Tip key={gp.id} text={gp.label} position="top">
                    <PopupTabs.Item onClick={() => onScrollGoto(i)} selected={activeTab === i}>
                        {typeof (gp.icon) === 'string' ? (
                            <Icon size="2" src={gp.icon} />
                        ) : (
                            React.createElement(gp.icon)
                        )}
                    </PopupTabs.Item>
                </Tip>
            )}
        </PopupTabs>
        <Flexer height=".5" />
        {children}
        <Flexer flex grow padding=".5 .25 .5 .5">
            <XScrollbars ref={scroller} className="grow" onScrollFrame={onScroll} >
                <Flexer padding="0 .75 0 0">
                    {model.map((gp, i) =>
                        <div key={`${gp.type}/${gp.id}`} ref={anchors[i]} data-tab={i}>
                            {gp.sets.map(st => <div key={`${st.type}/${st.id}`} >
                                {!!st.label &&
                                    <TitleLine>
                                        <Title size="xs" color={Colors.textLight} weight={500}>{st.label}</Title>
                                    </TitleLine>
                                }
                                <ItemSetFrame columns={st.columns}>
                                    {st.items.map(tm => <ItemMount key={tm.id} item={tm} onSelect={onSelectItem} />)}
                                </ItemSetFrame>
                                {i < (model.length - 1) && <Spacer f={1} />}
                            </div>)}
                        </div>
                    )}
                </Flexer>
            </XScrollbars>
        </Flexer>
    </Frame>;
};

export const ElementSelector = Object.assign(Impl, {
    Frame,
    Item,
    ItemSet,
    ItemGroup,
    ItemPreview,
    Model,
});

export type ElementSelectorItem = ReturnType<typeof Item>;
export type ElementSelectorItemSet = ReturnType<typeof ItemSet>;
export type ElementSelectorItemGroup = ReturnType<typeof ItemGroup>;
export type ElementSelectorModel = Model;
